import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Building2, Users, FileText, CheckCircle, Play,
  ArrowRight, Book, Shield, Coffee, MessageSquare
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';

const translations = {
  es: {
    title: 'Bienvenido a tu Onboarding Virtual',
    description: 'Comencemos con tu proceso de incorporación a la empresa',
    modules: {
      welcome: {
        title: 'Bienvenida y Orientación',
        description: 'Conoce nuestra empresa, cultura y valores'
      },
      policies: {
        title: 'Políticas y Procedimientos',
        description: 'Aprende sobre nuestras políticas y procedimientos clave'
      },
      tools: {
        title: 'Configuración de Herramientas',
        description: 'Configura tus herramientas y accesos'
      },
      training: {
        title: 'Capacitación Inicial',
        description: 'Completa tu capacitación inicial'
      }
    },
    progress: {
      title: 'Tu Progreso',
      completed: 'Completado',
      inProgress: 'En progreso',
      pending: 'Pendiente'
    },
    nextSteps: {
      title: 'Próximos Pasos',
      description: 'Completa estas tareas para continuar con tu onboarding'
    },
    support: {
      title: 'Soporte',
      description: 'Estamos aquí para ayudarte',
      contact: 'Contactar soporte'
    }
  },
  en: {
    title: 'Welcome to your Virtual Onboarding',
    description: 'Let\'s start with your company onboarding process',
    modules: {
      welcome: {
        title: 'Welcome and Orientation',
        description: 'Learn about our company, culture, and values'
      },
      policies: {
        title: 'Policies and Procedures',
        description: 'Learn about our key policies and procedures'
      },
      tools: {
        title: 'Tools Setup',
        description: 'Set up your tools and access'
      },
      training: {
        title: 'Initial Training',
        description: 'Complete your initial training'
      }
    },
    progress: {
      title: 'Your Progress',
      completed: 'Completed',
      inProgress: 'In progress',
      pending: 'Pending'
    },
    nextSteps: {
      title: 'Next Steps',
      description: 'Complete these tasks to continue your onboarding'
    },
    support: {
      title: 'Support',
      description: 'We\'re here to help',
      contact: 'Contact support'
    }
  }
};

const OnboardingPage = () => {
  const [currentModule, setCurrentModule] = useState(0);
  const { language } = useLanguage();
  const t = translations[language];
  const navigate = useNavigate();

  const modules = [
    {
      id: 'welcome',
      title: t.modules.welcome.title,
      description: t.modules.welcome.description,
      status: 'completed',
      icon: Building2
    },
    {
      id: 'policies',
      title: t.modules.policies.title,
      description: t.modules.policies.description,
      status: 'in-progress',
      icon: Shield
    },
    {
      id: 'tools',
      title: t.modules.tools.title,
      description: t.modules.tools.description,
      status: 'pending',
      icon: FileText
    },
    {
      id: 'training',
      title: t.modules.training.title,
      description: t.modules.training.description,
      status: 'pending',
      icon: Book
    }
  ];

  const nextSteps = [
    'Complete company culture training',
    'Review employee handbook',
    'Set up your workstation',
    'Schedule welcome meeting'
  ];

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-foreground">
            {t.title}
          </h1>
          <p className="mt-1 text-foreground/70">
            {t.description}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Onboarding Modules */}
            <div className="space-y-6">
              {modules.map((module, index) => (
                <div
                  key={module.id}
                  className="dark-card p-6 hover:border-accent/50 transition-all duration-300"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <module.icon className="h-6 w-6 text-accent" />
                      </div>
                      <div className="ml-4">
                        <h3 className="text-lg font-medium text-foreground">
                          {module.title}
                        </h3>
                        <p className="mt-1 text-foreground/70">
                          {module.description}
                        </p>
                      </div>
                    </div>
                    <div>
                      {module.status === 'completed' && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <CheckCircle className="h-4 w-4 mr-1" />
                          {t.progress.completed}
                        </span>
                      )}
                      {module.status === 'in-progress' && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          <Play className="h-4 w-4 mr-1" />
                          {t.progress.inProgress}
                        </span>
                      )}
                      {module.status === 'pending' && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          {t.progress.pending}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="mt-4 flex justify-end">
                    <button className="inline-flex items-center text-accent hover:text-accent/90">
                      {module.status === 'completed' ? 'Review' : 'Continue'}
                      <ArrowRight className="ml-1 h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-8">
            {/* Progress Card */}
            <div className="dark-card p-6">
              <h3 className="text-lg font-medium text-foreground mb-4">
                {t.progress.title}
              </h3>
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm text-foreground/70">Overall Progress</span>
                    <span className="text-sm font-medium text-foreground">25%</span>
                  </div>
                  <div className="h-2 bg-secondary rounded-full">
                    <div className="h-2 bg-accent rounded-full" style={{ width: '25%' }}></div>
                  </div>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-foreground/70">Modules Completed:</span>
                  <span className="text-foreground">1/4</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-foreground/70">Time Remaining:</span>
                  <span className="text-foreground">2 days</span>
                </div>
              </div>
            </div>

            {/* Next Steps */}
            <div className="dark-card p-6">
              <h3 className="text-lg font-medium text-foreground mb-4">
                {t.nextSteps.title}
              </h3>
              <div className="space-y-4">
                {nextSteps.map((step, index) => (
                  <div key={index} className="flex items-start">
                    <div className="flex-shrink-0">
                      <div className="h-6 w-6 rounded-full bg-accent/10 flex items-center justify-center text-accent">
                        {index + 1}
                      </div>
                    </div>
                    <span className="ml-3 text-foreground">{step}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Support */}
            <div className="dark-card p-6">
              <h3 className="text-lg font-medium text-foreground mb-2">
                {t.support.title}
              </h3>
              <p className="text-foreground/70 mb-4">
                {t.support.description}
              </p>
              <button className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-accent hover:bg-accent/90">
                <MessageSquare className="h-4 w-4 mr-2" />
                {t.support.contact}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingPage;